/**
 * Main entry point for the 3D Virtual Tour application
 */

import * as THREE from "three"
import { ModelLoader } from "./loaders/ModelLoader.js"
import { PanoramaLoader } from "./loaders/PanoramaLoader.js"
import { PanoramaConfigLoader } from "./loaders/PanoramaConfigLoader.js"
import { ModelView } from "./scenes/ModelView.js"
import { PanoramaView } from "./scenes/PanoramaView.js"
import { ViewManager } from "./scenes/ViewManager.js"
import { Minimap } from "./ui/Minimap.js"
import { DiagnosticHelper } from "./utils/DiagnosticHelper.js"
import { CustomizationUI } from "./ui/CustomizationUI.js"
import { FeatureHighlightManager } from "./controls/FeatureHighlightManager.js"
import { FeatureInfoPanel } from "./ui/FeatureInfoPanel.js"
import { API_URL } from "./config/env.js"

// Enable diagnostic mode to help with alignment
const DIAGNOSTIC_MODE = true // Set to false in production

// Application state
const app = {
  scene: null,
  camera: null,
  renderer: null,
  model: null,

  // Loaders
  modelLoader: null,
  panoramaLoader: null,

  // Views
  modelView: null,
  panoramaView: null,
  viewManager: null,

  // UI
  minimap: null,
  diagnostic: null,
  featureManager: null,
  featureInfoPanel: null,

  // State
  isLoading: true,
  loadingProgress: 0,

  // Config
  panoramaConfig: null,
}

/**
 * Find first panorama that has overlays configured
 */
async function findPanoramaWithOverlays(tourId) {
  try {
    // Check each panorama for overlays
    for (const panoId of Object.keys(app.panoramaConfig.panoramas)) {
      // Build API URL with tourId if available
      const url = tourId
        ? `${API_URL}/virtual-tour/tours/${tourId}/overlays/${panoId}`
        : `${API_URL}/overlays/${panoId}`

      const response = await fetch(url)
      if (response.ok) {
        const overlays = await response.json()
        if (overlays && overlays.length > 0) {
          console.log(
            `✅ Found panorama ${panoId} with ${overlays.length} overlay(s)`,
          )
          return panoId
        }
      }
    }
  } catch (error) {
    console.warn("Error checking for overlays:", error)
  }
  return null
}

/**
 * Handle URL parameters for testing/direct access
 * @returns {Promise<boolean>} True if URL parameters were handled
 */
async function handleURLParameters() {
  const urlParams = new URLSearchParams(window.location.search)
  const panoId = urlParams.get("pano")
  const overlayParam = urlParams.get("overlay")

  if (!panoId) return false

  // Wait a bit for everything to settle
  await new Promise((resolve) => setTimeout(resolve, 1000))

  // Enter the specified panorama
  if (app.panoramaConfig.panoramas[panoId]) {
    await app.viewManager.enterPanorama(panoId)

    // Apply overlay if specified
    if (overlayParam) {
      const [itemName, colorName] = overlayParam.split(":")
      if (itemName && colorName) {
        console.log(`🎨 Auto-loading overlay: ${itemName}:${colorName}`)

        // Wait for panorama to load
        await new Promise((resolve) => setTimeout(resolve, 500))

        // Load overlay options for this panorama
        await app.panoramaView.customizationManager.loadCustomizationOptions(
          panoId,
        )

        // Find the overlay configuration
        const items = app.panoramaView.customizationManager.availableItems
        const item = items.find((i) => i.name === itemName)

        if (item) {
          const color = item.colors.find((c) => c.name === colorName)
          if (color) {
            // Apply the overlay
            await app.panoramaView.customizationManager.selectColor(
              itemName,
              color,
              app.camera.position,
              app.panoramaConfig.panoramas[panoId].panoRotY,
            )

            // Update UI
            if (app.panoramaView.customizationUI) {
              app.panoramaView.customizationUI.refresh()
            }

            showNotification(
              `🎨 Viewing: ${item.label} - ${color.label}`,
              "success",
            )
          } else {
            console.warn(`⚠️ Color not found: ${colorName}`)
            showNotification(`⚠️ Overlay color not found: ${colorName}`, "info")
          }
        } else {
          console.warn(`⚠️ Item not found: ${itemName}`)
          showNotification(`⚠️ Overlay item not found: ${itemName}`, "info")
        }
      }
    }
  } else {
    console.warn(`⚠️ Panorama not found: ${panoId}`)
    showNotification(`⚠️ Panorama not found: ${panoId}`, "info")
  }

  return true // URL parameters were handled
}

/**
 * Parse URL parameters
 */
function parseURLParameters() {
  const urlParams = new URLSearchParams(window.location.search)

  // Parse view visibility options
  let showWalkthrough = true
  let showModel = true

  // Check for 'views' shorthand parameter
  const viewsParam = urlParams.get("views")
  if (viewsParam) {
    switch (viewsParam.toLowerCase()) {
      case "walkthrough":
        showWalkthrough = true
        showModel = false
        break
      case "model":
        showWalkthrough = false
        showModel = true
        break
      case "both":
        showWalkthrough = true
        showModel = true
        break
      case "none":
        showWalkthrough = false
        showModel = false
        break
    }
  }

  // Individual parameters override the 'views' shorthand
  if (urlParams.has("showWalkthrough")) {
    showWalkthrough = urlParams.get("showWalkthrough") === "true"
  }
  if (urlParams.has("showModel")) {
    showModel = urlParams.get("showModel") === "true"
  }

  // Parse customize options visibility (defaults to true)
  let showCustomize = true
  if (urlParams.has("showCustomize")) {
    showCustomize = urlParams.get("showCustomize") === "true"
  }

  return {
    tourId: urlParams.get("tourId") || urlParams.get("tour"), // Support both 'tourId' and 'tour'
    theme: urlParams.get("theme") || "light",
    view: urlParams.get("view") || "model",
    pano: urlParams.get("pano"),
    autostart: urlParams.get("autostart") === "true",
    overlay: urlParams.get("overlay"),
    embed: urlParams.get("embed"),
    lang: urlParams.get("lang") || "en",
    showWalkthrough,
    showModel,
    showCustomize,
  }
}

/**
 * Apply theme from tour configuration
 */
function applyTheme(tourConfig, themeName) {
  if (!tourConfig || !tourConfig.themes) return

  const theme = tourConfig.themes[themeName]
  if (!theme) {
    console.warn(`⚠️ Theme "${themeName}" not found, using default`)
    return
  }

  console.log(`🎨 Applying theme: ${themeName}`, theme)

  // Apply CSS variables
  const root = document.documentElement
  if (theme.colors) {
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value)
    })
  }

  // Apply custom CSS if provided
  if (theme.customCSS) {
    const styleEl = document.createElement("style")
    styleEl.textContent = theme.customCSS
    document.head.appendChild(styleEl)
  }

  // Update scene background to match theme
  if (theme.colors.background) {
    app.scene.background = new THREE.Color(theme.colors.background)
  }
}

/**
 * Configure embed mode
 */
function configureEmbedMode(embedMode) {
  if (!embedMode) return

  console.log(`📺 Configuring embed mode: ${embedMode}`)

  const elementsToHide = {
    minimal: ["#logo-container", "#nav-info"],
    true: ["#logo-container"],
  }

  const hideList = elementsToHide[embedMode]
  if (hideList) {
    hideList.forEach((selector) => {
      const el = document.querySelector(selector)
      if (el) el.style.display = "none"
    })
  }
}

/**
 * Configure view visibility based on URL parameters
 */
function configureViewVisibility(showWalkthrough, showModel) {
  console.log(
    `👁️ Configuring view visibility: walkthrough=${showWalkthrough}, model=${showModel}`,
  )

  const btnWalkthrough = document.getElementById("btn-walkthrough")
  const btnModel = document.getElementById("btn-model")
  const modeSwitcher = document.getElementById("mode-switcher")

  // Hide/show individual buttons
  if (btnWalkthrough) {
    btnWalkthrough.style.display = showWalkthrough ? "block" : "none"
  }

  if (btnModel) {
    btnModel.style.display = showModel ? "block" : "none"
  }

  // Hide the entire mode switcher if both buttons are hidden
  if (modeSwitcher && !showWalkthrough && !showModel) {
    modeSwitcher.style.display = "none"
    console.log("🙈 Both view buttons hidden - hiding mode switcher")
  }

  // Log the configuration
  if (showWalkthrough && !showModel) {
    console.log("✅ Walkthrough-only mode enabled")
  } else if (!showWalkthrough && showModel) {
    console.log("✅ Model-only mode enabled")
  } else if (showWalkthrough && showModel) {
    console.log("✅ Both views enabled (default)")
  } else {
    console.log("⚠️ Warning: Both views disabled")
  }
}

/**
 * Initialize the application
 */
async function init() {
  console.log("🚀 Initializing Virtual Tour...")

  // Parse URL parameters
  const urlParams = parseURLParameters()
  console.log("🔗 URL Parameters:", urlParams)

  // Check if tour ID is provided
  if (!urlParams.tourId) {
    console.log("⚠️ No tour ID specified in URL parameters")
    showNoTourSpecifiedError()
    return // Stop initialization
  }

  // Create scene
  app.scene = new THREE.Scene()
  app.scene.background = new THREE.Color(0x1a1a2e)

  // Create camera
  app.camera = new THREE.PerspectiveCamera(
    75,
    window.innerWidth / window.innerHeight,
    0.1,
    1000,
  )

  // Create renderer
  const canvas = document.getElementById("canvas")
  app.renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
  })
  app.renderer.setSize(window.innerWidth, window.innerHeight)
  app.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

  // Set correct color space for proper color rendering
  app.renderer.outputColorSpace = THREE.SRGBColorSpace

  // No tone mapping for pre-processed photos (Realsee images are already tone-mapped)
  app.renderer.toneMapping = THREE.NoToneMapping
  app.renderer.toneMappingExposure = 1.0

  // Add lights for better model visibility (increased to match panorama brightness)
  const ambientLight = new THREE.AmbientLight(0xffffff, 1.5) // Increased for brighter model
  app.scene.add(ambientLight)

  const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0) // Increased for brighter model
  directionalLight.position.set(5, 5, 5)
  app.scene.add(directionalLight)

  // Add a second light from the opposite side
  const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.8) // Increased for brighter model
  directionalLight2.position.set(-5, 5, -5)
  app.scene.add(directionalLight2)

  // Initialize config loader and load tour configuration first
  const configLoader = new PanoramaConfigLoader(urlParams.tourId, urlParams.theme)

  // Load panorama configuration (dynamic or legacy)
  let baseUrl = ""
  let tourConfig = null

  try {
    updateLoadingProgress(5, "Loading tour configuration...")
    const config = await configLoader.load()
    app.panoramaConfig = config
    tourConfig = config.tourConfig

    // Set base URL for dynamic tours
    if (tourConfig) {
      baseUrl = tourConfig.assets.baseUrl
      console.log(`🌐 Using dynamic tour assets from: ${baseUrl}`)
      applyTheme(tourConfig, urlParams.theme)
      configureEmbedMode(urlParams.embed)
    }

    // Configure view visibility based on URL parameters
    configureViewVisibility(urlParams.showWalkthrough, urlParams.showModel)
  } catch (error) {
    console.error("❌ Failed to load tour configuration:", error)

    // Check if this is a 404 error (tour not found)
    // Check both the error flag and the message content
    if (
      error.is404 ||
      error.status === 404 ||
      error.message.includes("not found") ||
      error.message.includes("404")
    ) {
      // Show error screen and stop loading
      console.log("🚫 Tour not found - showing 404 error screen")
      showTourNotFoundError(urlParams.tourId, error.message)
      return // Stop initialization completely
    }

    // For other errors (not 404), show notification and try to continue
    showNotification(`⚠️ Failed to load tour: ${error.message}`, "info")

    // If dynamic tour fails (but not 404), try to continue with legacy mode
    if (urlParams.tourId) {
      console.log("⚠️ Falling back to legacy mode")
      app.panoramaConfig = {
        panoramas: {},
        modelInfo: {
          file: "assets/models/output_00.gltf",
          bounds: {
            x: { min: -1.395, max: 1.12 },
            y: { min: -3.407, max: 2.448 },
            z: { min: -1.206, max: 0.971 },
          },
        },
        defaultCameraHeight: 0,
      }
    }

    // Configure view visibility even in fallback mode
    configureViewVisibility(urlParams.showWalkthrough, urlParams.showModel)
  }

  // Initialize loaders with base URL and tour ID
  app.modelLoader = new ModelLoader(baseUrl)
  app.panoramaLoader = new PanoramaLoader(
    baseUrl,
    urlParams.tourId,
    app.panoramaConfig.panoramas,
  )

  // Log panorama count
  const panoCount = Object.keys(app.panoramaConfig.panoramas).length
  console.log(`✅ Loaded ${panoCount} calibrated panoramas`)

  if (panoCount === 0) {
    showNotification(
      "⚠️ No calibrated panoramas found. Please use the calibration tool first.",
      "info",
    )
  }

  // Load the model
  try {
    updateLoadingProgress(10, "Loading 3D model...")

    // Use model path from tour config if available, otherwise use default
    const modelPath = tourConfig
      ? app.panoramaConfig.modelInfo.file
      : "models/output_00.gltf"

    console.log(`📦 Loading model: ${modelPath}`)

    app.model = await app.modelLoader.load(modelPath, (percent) => {
      updateLoadingProgress(10 + percent * 0.6, "Loading 3D model...")
    })
    app.scene.add(app.model)

    updateLoadingProgress(70, "Initializing views...")

    // Initialize views
    app.modelView = new ModelView(
      app.scene,
      app.camera,
      app.renderer,
      app.model,
      app.panoramaConfig,
    )

    app.panoramaView = new PanoramaView(
      app.scene,
      app.camera,
      app.renderer,
      app.model,
      app.panoramaLoader,
      app.panoramaConfig,
    )

    // Initialize view manager
    app.viewManager = new ViewManager(app.modelView, app.panoramaView)

    // Override the enterPanorama method to update button states
    const originalEnterPanorama = app.viewManager.enterPanorama.bind(
      app.viewManager,
    )
    app.viewManager.enterPanorama = async function (panoId, options) {
      await originalEnterPanorama(panoId, options)
      updateModeButtons("panorama")
    }

    await app.viewManager.initialize()

    updateLoadingProgress(85, "Initializing UI...")

    // Initialize customization UI (conditionally based on URL parameter)
    if (urlParams.showCustomize) {
      const customizationUI = new CustomizationUI(
        document.body,
        (itemName, colorConfig) => {
          if (colorConfig === null) {
            // Reset to original
            app.panoramaView.customizationManager.removeOverlay(itemName)
          } else {
            // Apply color
            app.panoramaView.customizationManager.selectColor(
              itemName,
              colorConfig,
              app.camera.position,
              app.panoramaConfig.panoramas[app.panoramaView.currentPanoId]
                .panoRotY,
            )
          }
        },
      )
      customizationUI.create()
      app.panoramaView.customizationUI = customizationUI
    } else {
      console.log("🚫 Customization UI hidden via URL parameter")
    }

    // Initialize diagnostic helper if enabled
    if (DIAGNOSTIC_MODE) {
      console.log("🔧 DIAGNOSTIC MODE ENABLED")
      app.diagnostic = new DiagnosticHelper(app.scene)
      app.diagnostic.addAxesHelper(2)
      app.diagnostic.addGridHelper(5, 20)
      app.diagnostic.checkAlignment(
        app.panoramaConfig.modelInfo.bounds,
        app.panoramaConfig.panoramas,
      )
      app.diagnostic.suggestFixes(
        app.panoramaConfig.modelInfo.bounds,
        app.panoramaConfig.panoramas,
      )

      // Show all panorama positions (with model offset to account for repositioning)
      Object.entries(app.panoramaConfig.panoramas).forEach(([id, pano]) => {
        app.diagnostic.showPanoramaPosition(
          id,
          pano.position,
          0x00ff00,
          app.model.position,
        )
      })

      // Hide diagnostic helpers by default
      app.diagnostic.helpers.forEach((helper) => {
        helper.visible = false
      })

      console.log("💡 Press D to toggle diagnostic helpers (hidden by default)")
      console.log("💡 Press T to test raycast from current camera position")
    }

    // Initialize minimap - DISABLED (only used in calibration tool)
    // app.minimap = new Minimap(app.panoramaConfig);
    // app.minimap.onNavigate((panoId) => {
    //   if (app.viewManager.getCurrentMode() === 'panorama') {
    //     app.panoramaView.navigateTo(panoId);
    //   } else {
    //     app.viewManager.enterPanorama(panoId);
    //   }
    // });

    // Initialize feature highlighting system
    const tourId = urlParams.tourId
    app.featureManager = new FeatureHighlightManager(
      app.scene,
      app.camera,
      tourId,
    )
    app.featureInfoPanel = new FeatureInfoPanel()

    // Load features
    try {
      await app.featureManager.loadFeatures()
    } catch (error) {
      console.warn("⚠️ Failed to load features:", error)
      // Continue anyway - features are optional
    }

    // Set up feature change callback
    app.featureManager.onFeatureChange((feature) => {
      if (feature) {
        app.featureInfoPanel.show(feature)
      } else {
        app.featureInfoPanel.hide()
      }
    })

    console.log("✅ Feature highlighting system initialized")

    updateLoadingProgress(95, "Setting up controls...")

    // Set up event listeners
    setupEventListeners()

    updateLoadingProgress(100, "Ready!")

    // Hide loading screen
    setTimeout(() => {
      hideLoadingScreen()
    }, 500)

    // Start animation loop
    animate()

    console.log("✅ Initialization complete")

    // Preload all panoramas in the background (after loading screen is hidden)
    // This eliminates first-visit glitches by loading textures into GPU memory
    setTimeout(() => {
      app.panoramaView.preloadAllPanoramas()
    }, 1000)

    // Check if we should auto-start in walkthrough mode based on views parameter
    const shouldStartInWalkthrough =
      urlParams.showWalkthrough && !urlParams.showModel

    // Handle URL parameters for direct panorama/overlay access
    const urlHandled = await handleURLParameters()

    // If views=walkthrough is specified OR we have panoramas, immediately enter walkthrough mode
    if (
      !urlHandled &&
      Object.keys(app.panoramaConfig.panoramas).length > 0
    ) {
      // Try to find first panorama with overlays, but don't require it
      const panoWithOverlays = await findPanoramaWithOverlays(urlParams.tourId)

      // Use panorama with overlays if found, otherwise use nearest panorama
      let targetPanoId = panoWithOverlays
      
      if (!targetPanoId) {
        // No panorama with overlays found, use nearest panorama to camera
        targetPanoId = findNearestPanorama(
          app.camera.position,
          app.panoramaConfig.panoramas,
        )
      }

      if (targetPanoId) {
        // Simulate the exact behavior of clicking the "Walkthrough View" button
        // This ensures consistent behavior between auto-start and manual button click
        const nearestPanoId = findNearestPanorama(
          app.camera.position,
          app.panoramaConfig.panoramas,
        )
        
        if (nearestPanoId) {
          // Get the current camera viewing direction in model view
          const { rotY, rotX } = getCameraViewingDirection()
          
          // Enter the panorama maintaining horizontal direction but with level view
          await app.viewManager.enterPanorama(nearestPanoId, {
            initialRotY: rotY,
            initialRotX: 0, // Keep camera level instead of looking down
          })
          
          // Update button states to reflect panorama mode
          updateModeButtons("panorama")
          
          // Show welcome message only if not in walkthrough-only mode
          if (!shouldStartInWalkthrough) {
            showNotification(
              "👋 Welcome! Try customizing the items using the menu on the left.",
              "info",
            )
          }
        }
      }
    }
  } catch (error) {
    console.error("❌ Initialization failed:", error)
    updateLoadingProgress(0, "Error loading tour")
  }
}

/**
 * Update loading progress
 */
function updateLoadingProgress(percent, message) {
  const progressElement = document.querySelector(".loading-progress")
  const messageElement = document.querySelector(
    ".loading-content p:first-of-type",
  )

  if (progressElement) {
    progressElement.textContent = `${Math.round(percent)}%`
  }

  if (messageElement && message) {
    messageElement.textContent = message
  }

  app.loadingProgress = percent
}

/**
 * Hide loading screen
 */
function hideLoadingScreen() {
  const loadingScreen = document.getElementById("loading-screen")
  loadingScreen.classList.add("hidden")
  app.isLoading = false
}

/**
 * Update mode button states
 */
function updateModeButtons(mode) {
  const btnWalkthrough = document.getElementById("btn-walkthrough")
  const btnModel = document.getElementById("btn-model")

  if (btnWalkthrough && btnModel) {
    if (mode === "panorama") {
      btnWalkthrough.classList.add("active")
      btnModel.classList.remove("active")
    } else {
      btnWalkthrough.classList.remove("active")
      btnModel.classList.add("active")
    }
  }
}

/**
 * Find the nearest panorama to a given position
 */
function findNearestPanorama(position, panoramas) {
  let nearestId = null
  let nearestDistance = Infinity

  Object.entries(panoramas).forEach(([id, pano]) => {
    // Account for model repositioning offset
    const panoWorldPos = new THREE.Vector3(
      pano.position.x + app.model.position.x,
      pano.position.y + app.model.position.y,
      pano.position.z + app.model.position.z,
    )

    const distance = position.distanceTo(panoWorldPos)

    if (distance < nearestDistance) {
      nearestDistance = distance
      nearestId = id
    }
  })

  console.log(
    `🎯 Nearest panorama: ${nearestId} (distance: ${nearestDistance.toFixed(
      2,
    )})`,
  )
  return nearestId
}

/**
 * Get current camera viewing direction from model view
 * Returns rotation angles (rotY, rotX) in degrees
 */
function getCameraViewingDirection() {
  // Extract rotation directly from camera (OrbitControls manages this)
  // Convert from radians to degrees to match PanoramaControls convention
  let rotY = THREE.MathUtils.radToDeg(app.camera.rotation.y)
  let rotX = THREE.MathUtils.radToDeg(app.camera.rotation.x)

  // Normalize rotY to 0-360 range
  rotY = ((rotY % 360) + 360) % 360

  console.log(`🧭 Camera Euler rotation (direct from camera):`)
  console.log(`   rotY=${rotY.toFixed(1)}° (horizontal/yaw)`)
  console.log(`   rotX=${rotX.toFixed(1)}° (vertical/pitch)`)
  console.log(`   camera.rotation.order = '${app.camera.rotation.order}'`)
  console.log(
    `📹 Camera position: (${app.camera.position.x.toFixed(
      2,
    )}, ${app.camera.position.y.toFixed(2)}, ${app.camera.position.z.toFixed(
      2,
    )})`,
  )

  // Also log the direction vector for debugging
  const direction = new THREE.Vector3()
  app.camera.getWorldDirection(direction)
  console.log(
    `➡️ Looking direction vector: (${direction.x.toFixed(
      3,
    )}, ${direction.y.toFixed(3)}, ${direction.z.toFixed(3)})`,
  )

  return { rotY, rotX }
}

/**
 * Set up event listeners
 */
function setupEventListeners() {
  // Window resize
  window.addEventListener("resize", onWindowResize)

  // Mode switcher buttons
  const btnWalkthrough = document.getElementById("btn-walkthrough")
  const btnModel = document.getElementById("btn-model")

  console.log("🔘 Mode switcher buttons:", { btnWalkthrough, btnModel })

  if (btnWalkthrough && btnModel) {
    // Set initial state - will be updated if we auto-start in walkthrough
    updateModeButtons("model")

    console.log(
      "✅ Mode switcher buttons initialized and event listeners attached",
    )

    btnWalkthrough.addEventListener("click", async () => {
      console.log("🖱️ Walkthrough View button clicked")
      console.log(`   Current mode: ${app.viewManager.getCurrentMode()}`)

      if (app.viewManager.getCurrentMode() === "model") {
        // Find nearest panorama to current camera position
        if (app.panoramaConfig && app.panoramaConfig.panoramas) {
          console.log(`   Searching for nearest panorama...`)
          console.log(
            `   Camera position: (${app.camera.position.x.toFixed(
              2,
            )}, ${app.camera.position.y.toFixed(
              2,
            )}, ${app.camera.position.z.toFixed(2)})`,
          )

          const nearestPanoId = findNearestPanorama(
            app.camera.position,
            app.panoramaConfig.panoramas,
          )

          console.log("woo", nearestPanoId)

          if (nearestPanoId) {
            // Get the current camera viewing direction in model view
            const { rotY, rotX } = getCameraViewingDirection()

            console.log(`🎬 Entering nearest panorama ${nearestPanoId}`)
            console.log(
              `   Keeping rotY=${rotY.toFixed(
                1,
              )}° (horizontal), setting rotX=0° (level view)`,
            )

            // Enter the nearest panorama maintaining horizontal direction but with level view
            await app.viewManager.enterPanorama(nearestPanoId, {
              initialRotY: rotY,
              initialRotX: 0, // Keep camera level instead of looking down
            })
          } else {
            console.error("❌ No nearest panorama found!")
            showNotification("No panoramas available", "info")
          }
        } else {
          console.error("❌ No panorama config available!")
          showNotification("Panorama configuration not loaded", "info")
        }
      } else {
        console.log("ℹ️ Already in panorama mode, no action needed")
      }
    })

    btnModel.addEventListener("click", () => {
      console.log(
        `🖱️ Model View button clicked. Current mode: ${app.viewManager.getCurrentMode()}`,
      )

      try {
        if (app.viewManager.getCurrentMode() === "panorama") {
          console.log("🔄 Exiting panorama, returning to model view...")
          // Return to model view
          app.viewManager.exitToModel()
          // app.minimap.hide(); // DISABLED
          updateModeButtons("model")
          console.log("✅ Should now be in model view")
        } else {
          console.log("ℹ️ Already in model mode, no action needed")
        }
      } catch (error) {
        console.error("❌ Error in Model View button handler:", error)
        showNotification("Error returning to model view", "info")
      }
    })
  }

  // Keyboard shortcuts
  window.addEventListener("keydown", (e) => {
    switch (e.key.toLowerCase()) {
      case "escape":
        // Exit to model view
        if (app.viewManager.getCurrentMode() === "panorama") {
          app.viewManager.exitToModel()
          // app.minimap.hide(); // DISABLED
          updateModeButtons("model")
        }
        break
      // case 'm':
      //   // Toggle minimap - DISABLED
      //   if (app.viewManager.getCurrentMode() === 'panorama') {
      //     app.minimap.toggle();
      //   }
      //   break;
      case "h":
      case "?":
        // Show help
        showHelp()
        break
      case "d":
        // Toggle diagnostic helpers
        if (app.diagnostic && DIAGNOSTIC_MODE) {
          app.diagnostic.helpers.forEach((helper) => {
            helper.visible = !helper.visible
          })
          console.log("🔧 Diagnostic helpers toggled")
        }
        break
      case "t":
        // Test raycast from current position
        if (app.diagnostic && DIAGNOSTIC_MODE && app.model) {
          app.diagnostic.testRaycast(app.camera.position, app.model)
        }
        break
      case "=":
      case "+":
        // Increase brightness (shader multiplier)
        if (app.panoramaView && app.panoramaView.currentPanoramaGroup) {
          const mesh = app.panoramaView.currentPanoramaGroup.children[0]
          if (mesh && mesh.material && mesh.material.uniforms) {
            mesh.material.uniforms.brightness.value = Math.min(
              mesh.material.uniforms.brightness.value + 0.1,
              2.0,
            )
            console.log(
              `💡 Brightness: ${mesh.material.uniforms.brightness.value.toFixed(
                2,
              )}`,
            )
            showNotification(
              `Brightness: ${mesh.material.uniforms.brightness.value.toFixed(
                2,
              )}`,
              "info",
            )
          }
        }
        break
      case "-":
      case "_":
        // Decrease brightness (shader multiplier)
        if (app.panoramaView && app.panoramaView.currentPanoramaGroup) {
          const mesh = app.panoramaView.currentPanoramaGroup.children[0]
          if (mesh && mesh.material && mesh.material.uniforms) {
            mesh.material.uniforms.brightness.value = Math.max(
              mesh.material.uniforms.brightness.value - 0.1,
              0.5,
            )
            console.log(
              `💡 Brightness: ${mesh.material.uniforms.brightness.value.toFixed(
                2,
              )}`,
            )
            showNotification(
              `Brightness: ${mesh.material.uniforms.brightness.value.toFixed(
                2,
              )}`,
              "info",
            )
          }
        }
        break
    }
  })
}

/**
 * Handle window resize
 */
function onWindowResize() {
  app.camera.aspect = window.innerWidth / window.innerHeight
  app.camera.updateProjectionMatrix()
  app.renderer.setSize(window.innerWidth, window.innerHeight)
}

/**
 * Show notification message
 */
function showNotification(message, type = "info") {
  const notification = document.createElement("div")
  notification.style.cssText = `
    position: fixed;
    top: 80px;
    left: 50%;
    transform: translateX(-50%);
    background: ${
      type === "info" ? "rgba(33, 150, 243, 0.9)" : "rgba(76, 175, 80, 0.9)"
    };
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    z-index: 10000;
    animation: slideDown 0.3s ease;
  `
  notification.textContent = message

  document.body.appendChild(notification)

  setTimeout(() => {
    notification.style.animation = "fadeOut 0.3s ease"
    setTimeout(() => {
      document.body.removeChild(notification)
    }, 300)
  }, 3000)
}

/**
 * Show message when no tour is specified in URL
 */
function showNoTourSpecifiedError() {
  console.log("⚠️ Displaying no tour specified message")

  // Hide loading screen
  const loadingScreen = document.getElementById("loading-screen")
  if (loadingScreen) {
    loadingScreen.style.display = "none"
  }

  // Hide canvas
  const canvas = document.getElementById("canvas")
  if (canvas) {
    canvas.style.display = "none"
  }

  // Hide navigation/mode switcher buttons
  const modeSwitcher = document.querySelector(".mode-switcher")
  if (modeSwitcher) {
    modeSwitcher.style.display = "none"
  }

  // Hide any other UI elements
  const navInfo = document.getElementById("nav-info")
  if (navInfo) {
    navInfo.style.display = "none"
  }

  const logoContainer = document.getElementById("logo-container")
  if (logoContainer) {
    logoContainer.style.display = "none"
  }

  // Create message screen - minimal and clean
  const messageScreen = document.createElement("div")
  messageScreen.id = "no-tour-specified"
  messageScreen.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #1a1a2e;
    color: white;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    z-index: 9999;
  `

  messageScreen.innerHTML = `
    <div style="text-align: center;">
      <h1 style="font-size: 24px; margin: 0; font-weight: 400; color: rgba(255,255,255,0.9);">
        No tour specified
      </h1>
    </div>
  `

  document.body.appendChild(messageScreen)
}

/**
 * Show tour not found error screen (404)
 */
function showTourNotFoundError(tourId, errorMessage) {
  console.log("🚫 Displaying tour not found error screen")

  // Hide loading screen
  const loadingScreen = document.getElementById("loading-screen")
  if (loadingScreen) {
    loadingScreen.style.display = "none"
  }

  // Hide canvas
  const canvas = document.getElementById("canvas")
  if (canvas) {
    canvas.style.display = "none"
  }

  // Hide navigation/mode switcher buttons
  const modeSwitcher = document.querySelector(".mode-switcher")
  if (modeSwitcher) {
    modeSwitcher.style.display = "none"
  }

  // Hide any other UI elements
  const navInfo = document.getElementById("nav-info")
  if (navInfo) {
    navInfo.style.display = "none"
  }

  const logoContainer = document.getElementById("logo-container")
  if (logoContainer) {
    logoContainer.style.display = "none"
  }

  // Create error screen - minimal and clean
  const errorScreen = document.createElement("div")
  errorScreen.id = "tour-not-found-error"
  errorScreen.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #1a1a2e;
    color: white;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    z-index: 9999;
  `

  errorScreen.innerHTML = `
    <div style="text-align: center;">
      <h1 style="font-size: 24px; margin: 0; font-weight: 400; color: rgba(255,255,255,0.9);">
        404 - Tour not found
      </h1>
    </div>
  `

  document.body.appendChild(errorScreen)
}

/**
 * Show help overlay
 */
function showHelp() {
  const help = document.createElement("div")
  help.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.95);
    color: white;
    padding: 30px;
    border-radius: 12px;
    max-width: 500px;
    z-index: 10001;
    box-shadow: 0 8px 32px rgba(0,0,0,0.5);
  `

  help.innerHTML = `
    <h2 style="margin: 0 0 20px 0; color: #00ff88;">🎮 Controls</h2>

    <h3 style="margin: 15px 0 10px 0; color: #4488ff;">Model View</h3>
    <ul style="margin: 0; padding-left: 20px; line-height: 1.8;">
      <li><strong>Drag:</strong> Rotate model</li>
      <li><strong>Scroll:</strong> Zoom in/out</li>
      <li><strong>Right drag:</strong> Pan camera</li>
      <li><strong>Click marker:</strong> Enter panorama</li>
    </ul>

    <h3 style="margin: 15px 0 10px 0; color: #4488ff;">Panorama View</h3>
    <ul style="margin: 0; padding-left: 20px; line-height: 1.8;">
      <li><strong>Drag:</strong> Look around 360°</li>
      <li><strong>Click floor marker:</strong> Navigate to room</li>
      <li><strong>ESC:</strong> Return to model view</li>
    </ul>

    <h3 style="margin: 15px 0 10px 0; color: #4488ff;">General</h3>
    <ul style="margin: 0; padding-left: 20px; line-height: 1.8;">
      <li><strong>H or ?:</strong> Show this help</li>
      <li><strong>+ / -:</strong> Adjust brightness (exposure)</li>
    </ul>

    <button id="close-help" style="
      margin-top: 20px;
      width: 100%;
      padding: 12px;
      background: #00ff88;
      color: black;
      border: none;
      border-radius: 6px;
      font-weight: bold;
      cursor: pointer;
    ">Got it!</button>
  `

  document.body.appendChild(help)

  document.getElementById("close-help").addEventListener("click", () => {
    document.body.removeChild(help)
  })

  // Close on click outside
  setTimeout(() => {
    const closeOnClick = (e) => {
      if (!help.contains(e.target)) {
        document.body.removeChild(help)
        document.removeEventListener("click", closeOnClick)
      }
    }
    document.addEventListener("click", closeOnClick)
  }, 100)
}

/**
 * Animation loop
 */
function animate() {
  requestAnimationFrame(animate)

  if (!app.isLoading && app.viewManager) {
    // Update current view
    app.viewManager.update()

    // Update minimap - DISABLED
    // if (app.minimap && app.minimap.isVisible) {
    //   const currentPanoId = app.viewManager.getCurrentPanoramaId();
    //   if (currentPanoId) {
    //     app.minimap.setCurrentPanorama(currentPanoId);
    //   }
    // }

    // Update feature detection (only in panorama mode)
    if (app.viewManager.getCurrentMode() === "panorama" && app.featureManager) {
      app.featureManager.update()
    }

    // Update minimap visibility - DISABLED
    // if (app.viewManager.getCurrentMode() === 'panorama' && !app.minimap.isVisible) {
    //   app.minimap.show();
    // } else if (app.viewManager.getCurrentMode() === 'model' && app.minimap.isVisible) {
    //   app.minimap.hide();
    // }
  }

  // Render the scene
  app.renderer.render(app.scene, app.camera)
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init)
} else {
  init()
}

// Export for debugging
window.app = app
window.THREE = THREE
